import { Box, Checkbox, Typography, useTheme } from "@mui/material";

interface EmailPreferencesProps {
  email: string;
  preferences: string[];
  unsubscribeAll: boolean;
  handleCheckPreference: (type: string) => void;
  setUnsubscribeAll: (val: boolean) => void;
}

export const emailTypes = [
  { type: "engagement", label: "Engagement Emails", description: "Notifications when new users engage with your AI persona." },
  { type: "weekly_updates", label: "Weekly Updates", description: "Summaries of your AI persona’s recent activity and engagement." },
  { type: "course", label: "Course-Related Emails", description: "Updates, reminders, or announcements about enrolled courses." },
  { type: "new_features", label: "New Feature Announcements", description: "Information on new product features and improvements." },
  { type: "inactivity", label: "Inactivity Emails", description: "Reminders to revisit Soopra after periods of inactivity." }
];

const EmailPreferences: React.FC<EmailPreferencesProps> = ({
  email,
  preferences,
  unsubscribeAll,
  handleCheckPreference,
  setUnsubscribeAll
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h6">Email Preferences</Typography>
        <Typography>
          Please select the type of emails you would like to receive at <strong>{email}</strong>.
        </Typography>
      </Box>

      <Box sx={{
        paddingY: 2,
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        {emailTypes.map((email, index) => (
          <Box key={email.type} sx={{
            paddingX: 2,
            display: 'flex',
            flexDirection: 'row',
            gap: 1,
            pb: index !== emailTypes.length - 1 ? 2 : 0,
            borderBottom: index !== emailTypes.length - 1 ? `2px solid ${theme.palette.divider}` : 'none',
          }}>
            <Checkbox disabled={unsubscribeAll} checked={preferences.includes(email.type)}
              onChange={() => handleCheckPreference(email.type)} />
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="body1"><strong>{email.label}</strong></Typography>
              <Typography variant="body2">{email.description}</Typography>
            </Box>
          </Box>
        ))}
      </Box>

      <Box sx={{
        paddingY: 1,
        border: `2px solid ${theme.palette.divider}`,
        borderRadius: 2.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        <Box sx={{ paddingX: 2, display: 'flex', flexDirection: 'row', gap: 1 }}>
          <Checkbox onChange={() => setUnsubscribeAll(!unsubscribeAll)} checked={unsubscribeAll} />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="body1"><strong>Unsubscribe from all emails</strong></Typography>
            <Typography variant="body2">Some essential emails may still be sent based on your activity.</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EmailPreferences;
